import {useEffect, useRef, useState} from 'react';
import {
  AnimatePresence,
  motion,
  useDragControls,
  useMotionValue,
} from 'framer-motion';
import styles from './styles.module.css';
import usePreventScrollOnDrag from '@/components/soopi/hooks/usePreventScrollOnDrag';
import useLockBodyScroll from '@/components/soopi/hooks/useLockBodyScroll';

const SoopiIframeContainer = ({
  children,
  isResizing,
  height,
  dragPointStyle,
  iframeUrl,
  isLeft,
  previewOpen,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const dragControls = useDragControls();
  const [isIframeHover, setIsIframeHover] = useState(false);

  //스크롤 막기 위한 코드
  useLockBodyScroll(isIframeHover || previewOpen);
  usePreventScrollOnDrag((isDragging || isResizing) && !isIframeHover);

  const x = useMotionValue(isLeft ? 0 : window.innerWidth - 30);
  const y = useMotionValue(0);

  const handlePointerDown = (event) => {
    setIsDragging(true);
    dragControls.start(event);
  };

  // 제약 조건 설정
  const [constraints, setConstraints] = useState({
    top: -window.innerHeight + 94 + height,
    left: 30,
    right: window.innerWidth - 450,
    bottom: 0,
  });

  useEffect(() => {
    const handleResize = () => {
      // 현재 위치 가져오기
      const currentY = y.get();
      const topConstraint = -window.innerHeight + 94 + height;

      const maxY = window.innerHeight - height - 30;

      let newY = currentY;
      // 위쪽으로 나가는 경우
      if (currentY < topConstraint) {
        newY = topConstraint;
      }

      // 아래쪽으로 나가는 경우
      if (currentY > maxY) {
        newY = maxY;
      }

      if (newY !== currentY) {
        y.set(newY);
      }

      setConstraints((prev) => ({
        ...prev,
        top: -window.innerHeight + 94 + height,
      }));
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [height, y]);

  useEffect(() => {
    const handleResize = () => {
      const containerWidth = 393;

      // 현재 위치 가져오기
      const currentX = x.get();

      const leftConstraint = isLeft
        ? 30
        : window.innerWidth - containerWidth - 30;
      const rightConstraint = isLeft
        ? window.innerWidth - containerWidth - 30
        : 30;

      let newX = currentX;

      if (isLeft) {
        if (currentX < leftConstraint) newX = leftConstraint;
        if (currentX > rightConstraint) newX = rightConstraint;
      } else {
        if (currentX > leftConstraint) newX = leftConstraint;
        if (currentX < rightConstraint) newX = rightConstraint;
      }

      if (newX !== currentX) {
        x.set(newX);
      }

      setConstraints((prev) => ({
        ...prev,
        left: leftConstraint,
        right: rightConstraint,
      }));
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [x, isLeft]);

  useEffect(() => {
    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // 드래그 중 경계 확인 및 위치 조정
  const handleDrag = (event, info) => {
    document.body.style.WebkitUserSelect = 'none';
    const currentX = x.get();
    const currentY = y.get();

    // 경계를 벗어나면 값 조정
    if (isLeft) {
      if (currentX < constraints.left) x.set(constraints.left);
      if (currentX > constraints.right) x.set(constraints.right);
    } else {
      if (currentX > constraints.left) x.set(constraints.left);
      if (currentX < constraints.right) x.set(constraints.right);
    }

    if (currentY < constraints.top) y.set(constraints.top);
    if (currentY > constraints.bottom) y.set(constraints.bottom);
  };

  return (
    <AnimatePresence>
      <motion.div
        ref={containerRef}
        className={styles.iframeContainer}
        initial={{scale: 0.8, y: -60}}
        animate={{opacity: 1, scale: 1}}
        drag={!isResizing}
        dragControls={dragControls}
        dragElastic={0}
        dragMomentum={false}
        dragTransition={{bounceStiffness: 600, bounceDamping: 50, power: 0}}
        onDrag={handleDrag}
        exit={{opacity: 0, scale: 0.8, y: 300, transition: {duration: 0.3}}}
        transition={{duration: 0.3}}
        style={{
          height,
          pointerEvents: isResizing ? 'none' : 'auto',
          cursor: isDragging ? 'grabbing' : 'auto',
          x,
          y,
        }}
        dragPropagation={false}
        onMouseEnter={() => {
          if (!isDragging && !isResizing) {
            setIsIframeHover(true);
          }
        }}
        onMouseLeave={() => !isDragging && setIsIframeHover(false)}>
        <div
          className={styles.dragPointBar}
          onPointerDown={handlePointerDown}
        />
        <div
          className={`${styles.dragPoint} ${styles.dragPointLeft}`}
          onPointerDown={handlePointerDown}
        />
        <div
          className={styles.dragPoint}
          style={{
            left: `${dragPointStyle?.left}px`,
            width: `${dragPointStyle?.width}px`,
          }}
          onPointerDown={handlePointerDown}
        />
        <div
          className={`${styles.dragPoint} ${styles.dragPointRight}`}
          onPointerDown={handlePointerDown}
        />
        <iframe
          id='soopi-frame'
          ref={iframeRef}
          src={iframeUrl}
          className={styles.iframeContent}
          style={{
            pointerEvents: isDragging ? 'none' : 'auto',
          }}
          allow='microphone *'
        />

        {children}
      </motion.div>
    </AnimatePresence>
  );
};

export default SoopiIframeContainer;
